import axios from 'axios';
import { API } from './constants';

/**
 * El CV y el proyecto que el cliente YA tiene cargados, para precargar un
 * formulario de generación.
 *
 * POR QUÉ EXISTE ESTE ARCHIVO
 *   Reportado por el área: "tuvimos que adjuntar el CV de forma manual". El
 *   formulario del proyecto NIW precargaba el NOMBRE del cliente y nada más —
 *   y encima guardaba el CV en el dossier al terminar, así que alimentaba un
 *   depósito que después no leía.
 *
 *   Medido cuando se reportó: 152 de 174 clientes ya tenían un CV utilizable
 *   guardado (mediana de 5.781 caracteres). El dato estaba; la pantalla no lo
 *   pedía.
 *
 *   La misma falta estaba en la pantalla de patente. Por eso esto es una
 *   función compartida y no un `useEffect` copiado: el próximo módulo que pida
 *   un CV lo llama y ya, en vez de volver a olvidarse.
 *
 * QUÉ NO HACE
 *   No decide qué hacer con lo que devuelve. Precargar es de la pantalla,
 *   porque solo ella sabe si el operador ya escribió algo que no se puede
 *   pisar.
 *
 * @param {string} clientId
 * @returns {Promise<{hasDossier: boolean, cvText: string, projectText: string,
 *                    credentialsText: string, cvFilename: string,
 *                    petitionerName: string, cvLooksBinary: boolean}>}
 */
export async function fetchDossierPrefill(clientId) {
  const vacio = {
    hasDossier: false,
    cvText: '',
    projectText: '',
    credentialsText: '',
    cvFilename: '',
    petitionerName: '',
    cvLooksBinary: false,
  };
  if (!clientId) return vacio;

  try {
    const token = localStorage.getItem('token');
    // `include_text=true` es lo que trae el texto y no solo el resumen de
    // nombres de archivo. Sin ese parámetro la respuesta llega igual, con 200
    // y sin `cv_text`, así que la precarga fallaría en silencio.
    const { data } = await axios.get(
      `${API}/clients/${clientId}/dossier`,
      {
        params: { include_text: true },
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!data) return vacio;
    return {
      hasDossier: Boolean(data.has_dossier ?? (data.cv_text || data.project_text)),
      cvText: (data.cv_text || '').trim(),
      projectText: (data.project_text || '').trim(),
      credentialsText: (data.credentials_text || '').trim(),
      cvFilename: data.cv_filename || '',
      // El nombre LEGAL sacado del CV, cuando ya se construyó la hoja canónica
      // del cliente. Es mejor que el nombre de la cuenta para precargar el
      // campo de solicitante: la cuenta suele estar a nombre de quien contrata
      // y en mayúsculas, y el documento tiene que decir el nombre del pasaporte.
      petitionerName: (data.petitioner_name || '').trim(),
      // Una extracción vieja pudo haber guardado un .docx decodificado como
      // UTF-8. El backend lo detecta; acá se propaga para NO precargarlo.
      cvLooksBinary: Boolean(data.cv_looks_binary),
    };
  } catch (e) {
    // Que no haya dossier no es un error del operador y no puede romper la
    // pantalla: se sigue pudiendo adjuntar el CV a mano, que es lo que se
    // hacía antes de que esto existiera.
    console.warn('[dossier] no se pudo precargar:', e?.message || e);
    return vacio;
  }
}

/**
 * ¿Lo que trajo el dossier sirve para precargar el campo del CV?
 *
 * Dos frenos, y los dos importan porque un campo lleno se revisa MENOS que uno
 * vacío: el operador lo da por bueno y el documento termina escrito sobre un
 * CV que no existe.
 *
 *   · 100 caracteres, el mismo umbral que usa el backend para decidir si un
 *     `cv_text` es prosa legible o el resto de una extracción fallida.
 *   · Que el backend no lo haya marcado como binario. Hay CVs guardados por
 *     una extracción vieja que son un .docx decodificado como UTF-8: largos,
 *     ilegibles, y perfectamente capaces de pasar un umbral de longitud.
 */
export function esCvUtilizable(prefill) {
  if (typeof prefill === 'string') {
    return prefill.trim().length >= 100;
  }
  if (!prefill) return false;
  return !prefill.cvLooksBinary && (prefill.cvText || '').trim().length >= 100;
}
